<template>
  <div class="container-fluid pt-4 pb-5">
    <div class="d-flex justify-content-between align-items-center flex-wrap mb-3">
      <div>
        <h3 class="text-primary mb-0">{{ category ? category.name : 'Category' }} — Sales</h3>
        <small class="text-muted">{{ formatDate(dateRange.startDate) }} - {{ formatDate(dateRange.endDate) }}</small>
      </div>
      <a :href="backLink" class="btn btn-outline-secondary"><i class="bi bi-arrow-left"></i> Back to dashboard</a>
    </div>

    <div class="row mb-3">
      <div class="col-md-6">
        <date-range-picker v-model="dateRange" :auto-apply="true" :ranges="false" opens="right" :append-to-body="true" class="w-100">
          <template #input="picker">
            <span v-if="picker.startDate && picker.endDate">{{ formatDate(picker.startDate) }} - {{ formatDate(picker.endDate) }}</span>
            <span v-else><i class="bi bi-calendar-event"></i> Filter by date</span>
          </template>
        </date-range-picker>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

    <template v-else>
      <div class="row mb-4" style="row-gap:18px;">
        <div class="col-6 col-md-3">
          <div class="text-secondary small">Sales</div>
          <div class="h5 mb-0 font-weight-bold">{{ money(totals.revenue) }}</div>
        </div>
        <div class="col-6 col-md-3">
          <div class="text-secondary small">Qty sold</div>
          <div class="h5 mb-0 font-weight-bold">{{ num(totals.quantity) }}</div>
        </div>
        <div class="col-6 col-md-3">
          <div class="text-secondary small">Profit</div>
          <div class="h5 mb-0 font-weight-bold">{{ money(totals.profit) }}</div>
        </div>
        <div class="col-6 col-md-3">
          <div class="text-secondary small">Orders · Invoices</div>
          <div class="h5 mb-0 font-weight-bold">{{ totals.orders }} · {{ totals.invoices || 0 }}</div>
        </div>
      </div>

      <div v-if="totals.invoice_lines_without_cost > 0" class="alert alert-secondary">
        {{ totals.invoice_lines_without_cost }} invoiced line(s) have no buying price, so their profit isn't counted. Set the buying price in Inventory and it will be picked up.
      </div>

      <div v-if="trend.length > 1" class="card mb-4">
        <div class="card-body">
          <strong>Daily sales</strong>
          <dashboard-saleschart :chart-data="chartData" :chart-options="{ responsive: true }" :height="110" />
        </div>
      </div>

      <div class="card mb-4">
        <div class="card-body">
          <strong>Items in this category</strong>
          <small class="text-muted d-block mb-2">Click an item to see its activity over the same period</small>
          <div class="table-responsive">
            <table class="table table-striped table-hover mb-0">
              <thead>
                <tr><th>Item</th><th class="text-right">Qty</th><th class="text-right">Sales</th><th class="text-right">Profit</th><th class="text-right">% of category</th></tr>
              </thead>
              <tbody>
                <tr v-for="p in products" :key="p.product_id || p.name">
                  <td>
                    <a v-if="p.product_id" class="text-dark" :href="itemLink(p.product_id)">{{ p.name }}</a>
                    <span v-else>{{ p.name }} <span class="badge badge-secondary">Grouped</span></span>
                  </td>
                  <td class="text-right">{{ num(p.quantity) }}</td>
                  <td class="text-right">{{ money(p.revenue) }}</td>
                  <td class="text-right">{{ money(p.profit) }}</td>
                  <td class="text-right">{{ totals.revenue > 0 ? (p.revenue / totals.revenue * 100).toFixed(1) : 0 }}%</td>
                </tr>
                <tr v-if="!products.length"><td colspan="5" class="text-center text-muted">No sales in this category for the period</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div v-if="lines.length" class="card">
        <div class="card-body">
          <strong>Sales</strong>
          <small v-if="linesTruncated" class="text-muted d-block">Showing the latest 500 lines — narrow the dates to see all.</small>
          <div class="table-responsive mt-2">
            <table class="table table-striped table-hover mb-0">
              <thead>
                <tr><th>Date</th><th>Order</th><th>Item</th><th class="text-right">Qty</th><th class="text-right">Price</th><th class="text-right">Total</th><th>Sold by</th></tr>
              </thead>
              <tbody>
                <tr v-for="l in lines" :key="l.id">
                  <td>{{ formatDateTime(l.created_at) }}</td>
                  <td>
                    <a v-if="l.invoice_id" :href="'/invoices/' + l.invoice_id">{{ l.order }}</a>
                    <a v-else :href="'/orders?order-number=' + l.order + '&source=/analytics/category-sales'">{{ l.order }}</a>
                  </td>
                  <td>{{ l.item }}</td>
                  <td class="text-right">{{ num(l.quantity) }}</td>
                  <td class="text-right">{{ money(l.price) }}</td>
                  <td class="text-right">{{ money(l.total) }}</td>
                  <td class="text-capitalize">{{ l.sold_by || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import DateRangePicker from "vue2-daterange-picker/src/components/DateRangePicker";
import moment from "moment";

export default {
  name: "CategorySales",
  components: { DateRangePicker },

  data() {
    const params = new URLSearchParams(window.location.search);
    const start = params.get("start-date") || moment().format("YYYY-MM-DD");
    const end = params.get("end-date") || start;
    return {
      categoryId: params.get("category-id"),
      // /analytics/sales itself redirects to beta for companies without the classic dashboard.
      back: params.get("from") === "beta" ? "/analytics/beta" : "/analytics/sales",
      dateRange: { startDate: moment(start).toDate(), endDate: moment(end).toDate() },
      loading: true,
      error: null,
      category: null,
      totals: { revenue: 0, quantity: 0, profit: 0, orders: 0, invoices: 0, invoice_lines_without_cost: 0 },
      products: [],
      trend: [],
      lines: [],
      linesTruncated: false,
    };
  },

  computed: {
    startDate() { return moment(this.dateRange.startDate).format("YYYY-MM-DD"); },
    endDate() { return moment(this.dateRange.endDate).format("YYYY-MM-DD"); },
    backLink() { return `${this.back}?start-date=${this.startDate}&end-date=${this.endDate}`; },
    chartData() {
      return {
        labels: this.trend.map(t => moment(t.date).format("DD MMM")),
        datasets: [{ label: "Sales", backgroundColor: "#fd4719", borderColor: "#fd4719", data: this.trend.map(t => t.revenue) }],
      };
    },
  },

  watch: {
    dateRange: {
      deep: true,
      handler(r) {
        if (!r.startDate || !r.endDate) return;
        const p = new URLSearchParams(window.location.search);
        p.set("start-date", this.startDate);
        p.set("end-date", this.endDate);
        window.history.replaceState(null, null, "?" + p.toString());
        this.load();
      },
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    load() {
      if (!this.categoryId) {
        this.loading = false;
        this.error = "No category selected.";
        return;
      }
      this.loading = true;
      this.error = null;
      axios
        .get(`/api/sales-analytics/metrics/categories/${this.categoryId}/sales`, {
          params: { start_date: this.startDate, end_date: this.endDate },
        })
        .then(({ data }) => {
          this.category = data.category;
          this.totals = data.totals;
          this.products = data.products;
          this.trend = data.trend;
          this.lines = data.lines;
          this.linesTruncated = data.lines_truncated;
        })
        .catch(e => (this.error = e.response?.data?.message || "Could not load category sales."))
        .finally(() => (this.loading = false));
    },
    itemLink(productId) {
      return `/inventory-activity?only-sales=1&p-identifier-id=${productId}&start-date=${this.startDate}&end-date=${this.endDate}`;
    },
    money(v) {
      return "KES " + Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    num(v) {
      return Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
    },
    formatDate(d) {
      return d ? moment(d).format("DD-MMM-YYYY") : "";
    },
    formatDateTime(d) {
      return moment(d).format("HH:mm DD-MM-YYYY");
    },
  },
};
</script>
